import { IMediaFile } from "@/shared/types";
import { useAccountStore } from "@features/account-management";
import { ComponentType, ReactNode } from "react";
import { TElement } from "../../../types";
import { useGenerateImage, useResetStore } from "../hooks";
import { useAiImageStoreShallow } from "../store";
import { AiImageFeatureDisabled } from "./ai-image-feature-disabled";
import { AiImageGenerateProgress } from "./ai-image-generate-progress";
import { AiImageGeneratedPreview } from "./ai-image-generated-preview";
import { AiImageLimitations } from "./ai-image-limitations";
import { AiImagePromptArea } from "./ai-image-prompt-area";
import { AiImageTypes } from "./ai-image-types";

type TAiImageProps = {
  onSelect: (mediaFile: IMediaFile) => void;
  view: "dialog" | "panel";
  elementWrapper?:
    | ComponentType<{ children: ReactNode; element: TElement }>
    | undefined;
};

export const AiImage = ({ elementWrapper, onSelect, view }: TAiImageProps) => {
  const isAiImageFeatureDisabled = useAccountStore(
    (s) => s.account?.isAiImageFeatureDisabled,
  );
  useResetStore();

  const { mediaFile, setMediaFile } = useAiImageStoreShallow((s) => ({
    mediaFile: s.mediaFile,
    setMediaFile: s.setMediaFile,
  }));

  const { handleGenerateImage, isPending } = useGenerateImage();

  const showPromptForm = !isPending && !mediaFile;
  const showProgress = isPending;
  const showPreview = !isPending && mediaFile;

  return (
    <div className="tw-pt-2 tw-px-6 tw-pb-10 tw-relative">
      {isAiImageFeatureDisabled && <AiImageFeatureDisabled />}

      <div
        className={
          isAiImageFeatureDisabled ? "tw-opacity-20 tw-pointer-events-none" : ""
        }
      >
        {showProgress && <AiImageGenerateProgress />}

        {showPromptForm && (
          <>
            <h2 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-text-blue tw-font-sans tw-mt-0 tw-mb-6">
              What would you like to create?
              <span className="tw-flex tw-items-center tw-ml-3 tw-justify-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-blue-100 tw-font-semibold tw-text-blue-700 tw-text-[10px]/normal">
                Beta
              </span>
            </h2>
            <AiImagePromptArea
              handleGenerateImage={
                isAiImageFeatureDisabled ? () => {} : handleGenerateImage
              }
            />
            <AiImageTypes />
          </>
        )}

        {showPreview && (
          <AiImageGeneratedPreview
            elementWrapper={elementWrapper}
            mediaFile={mediaFile}
            setMediaFile={setMediaFile}
            handleGenerateImage={
              isAiImageFeatureDisabled ? () => {} : handleGenerateImage
            }
            onSelect={onSelect}
            view={view}
          />
        )}
      </div>

      <AiImageLimitations />
    </div>
  );
};
